import React, { useEffect, useState } from 'react';
import { homologacionesApi, entidadesApi, productosApi, contratosApi, exportApi } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Plus, Pencil, Trash2, ShieldCheck, AlertCircle, Download, Paperclip } from 'lucide-react';
import { toast } from 'sonner';
import HomologacionArchivosModal from '../components/homologaciones/HomologacionArchivosModal';
import { ConfirmDeleteDialog } from '../components/ui/ConfirmDeleteDialog';
import { useSortable } from '../hooks/useSortable';
import { SortableHeader } from '../components/ui/SortableHeader';
import { EmptyState } from '../components/ui/EmptyState';
import { formatFecha } from '../utils/dateFormat';

const ESTADO_COLOR = {
  en_proceso: 'bg-amber-100 text-amber-700',
  vigente: 'bg-emerald-100 text-emerald-700',
  caducada: 'bg-rose-100 text-rose-700',
  rechazada: 'bg-ink-200 text-ink-500',
  pendiente_renovacion: 'bg-orange-100 text-orange-700',
};
const ESTADOS = ['en_proceso', 'vigente', 'caducada', 'rechazada', 'pendiente_renovacion'];

export default function Homologaciones() {
  const { isAdmin, user } = useAuth();
  const canWrite = user?.rol !== 'agente_libre';
  const canDelete = isAdmin || user?.rol === 'dir_comercial';
  const canExport = user?.rol === 'admin' || user?.rol === 'dir_comercial';
  const [items, setItems] = useState([]);
  const [entidades, setEntidades] = useState([]);
  const [productos, setProductos] = useState([]);
  const [contratos, setContratos] = useState([]);
  const [estadoFilter, setEstadoFilter] = useState('__all__');
  const [caducidadFilter, setCaducidadFilter] = useState('__all__'); // 90, 180, 365
  const [dialog, setDialog] = useState({ open: false, data: null });
  const [archivosModal, setArchivosModal] = useState({ open: false, homologacion: null });
  const [confirmDel, setConfirmDel] = useState({ open: false, item: null });

  const { sorted, sortKey, sortDir, toggle } = useSortable(
    items,
    'entidad_nombre',
    'asc',
    {
      gestora: (h) => h.entidad_nombre || '',
      contrato: (h) => h.contrato_nombre || '',
      productos: (h) => (h.producto_ids || []).length,
      caducidad: (h) => h.fecha_caducidad || '',
      referencia: (h) => h.referencia_documento || '',
    }
  );

  const blank = {
    entidad_id: '', contrato_id: '', tipo: 'homologacion', estado: 'en_proceso',
    producto_ids: [], fecha_solicitud: '', fecha_resolucion: '', fecha_caducidad: '',
    referencia_documento: '', notas: '',
  };

  const load = async () => {
    const filters = {};
    if (estadoFilter !== '__all__') filters.estado = estadoFilter;
    if (caducidadFilter !== '__all__') filters.proxima_caducidad_dias = caducidadFilter;
    const [hs, es, prs, cs] = await Promise.all([
      homologacionesApi.list(filters),
      entidadesApi.list({ limit: 500 }),
      productosApi.list({ activo: true, limit: 500 }),
      contratosApi.list(),
    ]);
    setItems(hs); setEntidades(es); setProductos(prs); setContratos(cs);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [estadoFilter, caducidadFilter]);

  const save = async () => {
    try {
      const d = { ...dialog.data };
      for (const k of ['fecha_solicitud', 'fecha_resolucion', 'fecha_caducidad', 'contrato_id', 'referencia_documento']) {
        if (!d[k]) delete d[k];
      }
      if (!d.producto_ids?.length) { toast.error('Selecciona al menos un producto'); return; }
      if (d.id) {
        const id = d.id; delete d.id;
        delete d.entidad_id;
        await homologacionesApi.update(id, d);
        toast.success('Actualizada');
      } else {
        if (!d.entidad_id) { toast.error('Selecciona la gestora'); return; }
        await homologacionesApi.create(d);
        toast.success('Creada');
      }
      setDialog({ open: false, data: null });
      load();
    } catch (e) { toast.error(e.message); }
  };

  const remove = (item) => setConfirmDel({ open: true, item });
  const confirmarBorrar = async () => {
    if (!confirmDel.item) return;
    try { await homologacionesApi.delete(confirmDel.item.id); toast.success('Borrada'); load(); }
    catch (e) { toast.error(e.message); }
  };

  return (
    <div className="space-y-6" data-testid="homologaciones-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-3xl font-bold text-ink-900 font-display tracking-tight">Homologaciones y Acuerdos</h1>
          <p className="text-sm text-ink-500 mt-1">Homologaciones de productos, autorizaciones y acuerdos marco de precios — con archivos adjuntos.</p>
        </div>
        {canWrite && (
          <div className="flex flex-wrap gap-2">
            {canExport && (
              <Button variant="outline" onClick={async () => { try { await exportApi.descargar('homologaciones'); toast.success('CSV descargado'); } catch (e) { toast.error(e.message); } }} data-testid="btn-export-homologaciones">
                <Download className="w-4 h-4 mr-2" /> Exportar
              </Button>
            )}
            <Button onClick={() => setDialog({ open: true, data: blank })} data-testid="btn-nueva-homologacion">
              <Plus className="w-4 h-4 mr-2" /> Nueva homologación
            </Button>
          </div>
        )}
      </div>

      <div className="flex gap-3 flex-wrap items-center">
        <Select value={estadoFilter} onValueChange={setEstadoFilter}>
          <SelectTrigger className="w-56"><SelectValue placeholder="Estado" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="__all__">Todos los estados</SelectItem>
            {ESTADOS.map((e) => <SelectItem key={e} value={e}>{e.replace('_', ' ')}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={caducidadFilter} onValueChange={setCaducidadFilter}>
          <SelectTrigger className="w-64"><SelectValue placeholder="Caducidad" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="__all__">Cualquier caducidad</SelectItem>
            <SelectItem value="90">Caducan en ≤ 90 días</SelectItem>
            <SelectItem value="180">Caducan en ≤ 180 días</SelectItem>
            <SelectItem value="365">Caducan en ≤ 1 año</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="bg-white rounded-lg border border-ink-200 overflow-hidden">
        <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-ink-50 text-ink-600 uppercase text-xs">
            <tr>
              <SortableHeader label="Gestora"    sortKey="gestora"    currentKey={sortKey} dir={sortDir} onToggle={toggle} />
              <SortableHeader label="Contrato"   sortKey="contrato"   currentKey={sortKey} dir={sortDir} onToggle={toggle} />
              <SortableHeader label="Productos"  sortKey="productos"  currentKey={sortKey} dir={sortDir} onToggle={toggle} />
              <SortableHeader label="Tipo"       sortKey="tipo"       currentKey={sortKey} dir={sortDir} onToggle={toggle} />
              <SortableHeader label="Estado"     sortKey="estado"     currentKey={sortKey} dir={sortDir} onToggle={toggle} align="center" />
              <SortableHeader label="Caducidad"  sortKey="caducidad"  currentKey={sortKey} dir={sortDir} onToggle={toggle} />
              <SortableHeader label="Referencia" sortKey="referencia" currentKey={sortKey} dir={sortDir} onToggle={toggle} />
              <th className="w-20"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-ink-100">
            {sorted.length === 0 && (
              <tr>
                <td colSpan={8} className="px-0 py-0">
                  <EmptyState
                    compact
                    icon={ShieldCheck}
                    title="Sin homologaciones registradas"
                    description="Crea la primera homologación o ajusta los filtros."
                    testid="homologaciones-empty"
                  />
                </td>
              </tr>
            )}
            {sorted.map((h) => {
              const dias = h.dias_a_caducidad;
              const alerta = dias != null && dias < 90;
              return (
                <tr key={h.id} className="hover:bg-ink-50" data-testid={`hom-row-${h.id}`}>
                  <td className="px-4 py-3 font-medium">{h.entidad_nombre}</td>
                  <td className="px-4 py-3 text-ink-600 text-xs">{h.contrato_nombre || '—'}</td>
                  <td className="px-4 py-3">
                    <div className="flex flex-wrap gap-1">
                      {(h.productos_detalle || []).map((p) => (
                        <span key={p.id} className="text-[10px] font-mono bg-ink-100 text-ink-700 px-1.5 py-0.5 rounded">{p.referencia}</span>
                      ))}
                    </div>
                  </td>
                  <td className="px-4 py-3 text-xs text-ink-500">
                    {h.tipo === 'acuerdo_marco_precios' ? 'Acuerdo marco de precios' : h.tipo === 'autorizacion' ? 'Autorización' : 'Homologación'}
                  </td>
                  <td className="px-4 py-3 text-center">
                    <span className={`inline-block px-2 py-0.5 rounded text-xs ${ESTADO_COLOR[h.estado] || ''}`}>{h.estado.replace('_', ' ')}</span>
                  </td>
                  <td className={`px-4 py-3 ${alerta ? 'text-rose-700 font-semibold' : 'text-ink-600'}`}>
                    {h.fecha_caducidad ? (
                      <span className="flex items-center gap-1">
                        {alerta && <AlertCircle className="w-3.5 h-3.5" />}
                        {formatFecha(h.fecha_caducidad)}
                        {dias != null && <span className="text-[10px] ml-1">({dias < 0 ? `hace ${-dias}d` : `en ${dias}d`})</span>}
                      </span>
                    ) : '—'}
                  </td>
                  <td className="px-4 py-3 text-ink-500 font-mono text-xs">{h.referencia_documento || '—'}</td>
                  <td className="px-4 py-3 text-right">
                    {canWrite && (
                      <div className="flex gap-1 justify-end">
                        <Button variant="ghost" size="sm" onClick={() => setArchivosModal({ open: true, homologacion: h })} data-testid={`btn-hom-archivos-${h.id}`} title="Archivos adjuntos">
                          <Paperclip className="w-3.5 h-3.5" />
                          {h.num_archivos > 0 && <span className="text-[10px] ml-0.5 text-brand-600 font-semibold">{h.num_archivos}</span>}
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => setDialog({ open: true, data: { ...h } })}>
                          <Pencil className="w-3.5 h-3.5" />
                        </Button>
                        {canDelete && (
                          <Button variant="ghost" size="sm" className="text-rose-600" onClick={() => remove(h)}>
                            <Trash2 className="w-3.5 h-3.5" />
                          </Button>
                        )}
                      </div>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        </div>
      </div>

      {/* Dialog */}
      <Dialog open={dialog.open} onOpenChange={(v) => setDialog({ open: v, data: v ? dialog.data : null })}>
        <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><ShieldCheck className="w-4 h-4" />{dialog.data?.id ? 'Editar homologación' : 'Nueva homologación'}</DialogTitle>
          </DialogHeader>
          {dialog.data && (
            <div className="space-y-3">
              {!dialog.data.id && (
                <div>
                  <Label>Gestora *</Label>
                  <Select value={dialog.data.entidad_id || '__none__'} onValueChange={(v) => setDialog((d) => ({ ...d, data: { ...d.data, entidad_id: v === '__none__' ? '' : v } }))}>
                    <SelectTrigger><SelectValue placeholder="—" /></SelectTrigger>
                    <SelectContent className="max-h-60">
                      <SelectItem value="__none__">—</SelectItem>
                      {entidades.filter((e) => e.tipo === 'gestora' || e.tipo_principal === 'gestora').map((e) => (
                        <SelectItem key={e.id} value={e.id}>{e.nombre_comercial || e.razon_social}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Tipo</Label>
                  <Select value={dialog.data.tipo} onValueChange={(v) => setDialog((d) => ({ ...d, data: { ...d.data, tipo: v } }))}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="homologacion">Homologación (con ensayo)</SelectItem>
                      <SelectItem value="autorizacion">Autorización (ficha técnica)</SelectItem>
                      <SelectItem value="acuerdo_marco_precios">Acuerdo marco de precios</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Estado</Label>
                  <Select value={dialog.data.estado} onValueChange={(v) => setDialog((d) => ({ ...d, data: { ...d.data, estado: v } }))}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>{ESTADOS.map((e) => <SelectItem key={e} value={e}>{e.replace('_', ' ')}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </div>
              <div>
                <Label>Contrato (opcional)</Label>
                <Select value={dialog.data.contrato_id || '__none__'} onValueChange={(v) => setDialog((d) => ({ ...d, data: { ...d.data, contrato_id: v === '__none__' ? '' : v } }))}>
                  <SelectTrigger><SelectValue placeholder="—" /></SelectTrigger>
                  <SelectContent><SelectItem value="__none__">—</SelectItem>{contratos.map((c) => <SelectItem key={c.id} value={c.id}>{c.nombre}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label>Productos * (referencias propias)</Label>
                <div className="flex flex-wrap gap-1.5 p-2 border border-ink-200 rounded-md max-h-32 overflow-y-auto">
                  {productos.map((p) => {
                    const on = (dialog.data.producto_ids || []).includes(p.id);
                    return (
                      <button
                        key={p.id}
                        type="button"
                        onClick={() => setDialog((d) => {
                          const cur = d.data.producto_ids || [];
                          return { ...d, data: { ...d.data, producto_ids: cur.includes(p.id) ? cur.filter((x) => x !== p.id) : [...cur, p.id] } };
                        })}
                        className={`px-2 py-0.5 rounded text-xs ${on ? 'bg-brand-500 text-white' : 'bg-ink-100 text-ink-700 hover:bg-ink-200'}`}
                      >{p.referencia}</button>
                    );
                  })}
                </div>
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div><Label>Solicitud</Label><Input type="date" value={dialog.data.fecha_solicitud || ''} onChange={(e) => setDialog((d) => ({ ...d, data: { ...d.data, fecha_solicitud: e.target.value } }))} /></div>
                <div><Label>Resolución</Label><Input type="date" value={dialog.data.fecha_resolucion || ''} onChange={(e) => setDialog((d) => ({ ...d, data: { ...d.data, fecha_resolucion: e.target.value } }))} /></div>
                <div><Label>Caducidad</Label><Input type="date" value={dialog.data.fecha_caducidad || ''} onChange={(e) => setDialog((d) => ({ ...d, data: { ...d.data, fecha_caducidad: e.target.value } }))} /></div>
              </div>
              <div>
                <Label>Referencia documento</Label>
                <Input value={dialog.data.referencia_documento || ''} onChange={(e) => setDialog((d) => ({ ...d, data: { ...d.data, referencia_documento: e.target.value } }))} />
              </div>
              <div>
                <Label>Notas</Label>
                <Textarea rows={2} value={dialog.data.notas || ''} onChange={(e) => setDialog((d) => ({ ...d, data: { ...d.data, notas: e.target.value } }))} />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialog({ open: false, data: null })}>Cancelar</Button>
            <Button onClick={save} data-testid="btn-guardar-homologacion">Guardar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <HomologacionArchivosModal
        open={archivosModal.open}
        onOpenChange={(v) => setArchivosModal({ open: v, homologacion: v ? archivosModal.homologacion : null })}
        homologacion={archivosModal.homologacion}
        onChange={load}
      />

      <ConfirmDeleteDialog
        open={confirmDel.open}
        onOpenChange={(o) => setConfirmDel((s) => ({ ...s, open: o }))}
        resource="homologación"
        itemName={confirmDel.item?.referencia_documento || confirmDel.item?.entidad_nombre}
        onConfirm={confirmarBorrar}
        testidPrefix="confirm-del-homologacion"
      />
    </div>
  );
}
